import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, ArrowDown, Storefront, User, QrCode, Code, Gift, Coins,
  ShieldCheck, LockKey, Fingerprint, Wallet, CreditCard, Cardholder, Buildings,
  Tag, Megaphone, DeviceMobile, ChartLine, MapPin, Receipt, CheckCircle, Check, X,
  UsersThree, ScanSmiley, IdentificationBadge, Handshake, Sparkle, Stack, List,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { PhoneFrame, CustomerAppScreen, StaffAppScreen, DashboardMockup } from "@/components/marketing/Mockups";

const Eyebrow = ({ children }) => (
  <p className="text-primary font-mono text-xs tracking-[0.25em] uppercase mb-4">{children}</p>
);

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { href: "#customers", label: "For Customers", testid: "nav-for-customers" },
    { href: "#businesses", label: "For Businesses", testid: "nav-for-businesses" },
    { href: "#", label: "For Developers", testid: "nav-for-developers" },
  ];
  useEffect(() => {
    if (user) nav(homeFor(user.role));
  }, [user, nav]);

  return (
    <div className="min-h-screen ok-bg relative grain">
      {/* ---------- Header ---------- */}
      <header className="sticky top-0 z-40 bg-[hsl(160,42%,8%)]/95 backdrop-blur-md border-b border-white/10">
        <div className="relative flex items-center justify-between px-5 sm:px-10 py-2.5 max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <Logo variant="white" className="h-9 sm:h-11" />
          </div>
          <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((t) => (
              <a key={t.label} href={t.href} data-testid={t.testid}
                onClick={(e) => { if (t.href === "#") e.preventDefault(); }}
                className="inline-flex items-center whitespace-nowrap rounded-full px-4 h-9 text-sm font-medium text-white/75 hover:text-white hover:bg-white/10 transition-colors">
                {t.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login-btn" className="rounded-full text-white hover:bg-white/10 hover:text-white px-3 sm:px-4">Sign in</Button>
            </Link>
            <Link to="/register" className="hidden lg:block">
              <Button data-testid="nav-get-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Get OneKite</Button>
            </Link>
            <button data-testid="nav-mobile-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-white hover:bg-white/10 transition-colors">
              {menuOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-white/10 bg-[hsl(160,42%,8%)] px-5 py-4 space-y-1 fade-up">
            {navLinks.map((t) => (
              <a key={t.label} href={t.href} data-testid={`m-${t.testid}`}
                onClick={(e) => { if (t.href === "#") e.preventDefault(); setMenuOpen(false); }}
                className="block rounded-xl px-4 py-3 text-white/85 hover:bg-white/10 text-sm font-medium transition-colors">
                {t.label}
              </a>
            ))}
            <Link to="/register" onClick={() => setMenuOpen(false)} className="block pt-2">
              <Button data-testid="nav-mobile-get-btn" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">Get OneKite</Button>
            </Link>
          </div>
        )}
      </header>

      {/* ---------- Hero ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 pt-12 sm:pt-24 pb-16 sm:pb-20 grid lg:grid-cols-2 gap-10 sm:gap-12 items-center">
        <div>
          <h1 className="font-display font-extrabold text-[2.75rem] sm:text-6xl lg:text-7xl leading-[1.02] fade-up">
            One identity. <span className="text-primary">Every business.</span>
          </h1>
          <p className="mt-5 sm:mt-7 text-muted-foreground text-base sm:text-lg max-w-xl leading-relaxed fade-up" style={{ animationDelay: "80ms" }}>
            Earn rewards, manage memberships and connect with the businesses you love. All through one OneKite account.
          </p>
          <div className="mt-8 sm:mt-9 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: "140ms" }}>
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" data-testid="cta-customer-register" className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12 transition-all active:scale-95">
                Get OneKite <ArrowRight className="ml-2" size={18} weight="bold" />
              </Button>
            </Link>
            <Link to="/business/register" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" data-testid="cta-business-register" className="w-full sm:w-auto rounded-full border-black/15 bg-white/60 hover:bg-white px-7 h-12">
                Join as a Business
              </Button>
            </Link>
          </div>
        </div>
        <div className="fade-up" style={{ animationDelay: "200ms" }}>
          <PhoneFrame><CustomerAppScreen /></PhoneFrame>
        </div>
      </section>

      {/* ---------- The Problem ---------- */}
      <Section divider white>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">Your wallet doesn't need another loyalty card.</h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
            Most people juggle a different card, app and sign-up for every shop they love and forget half the rewards they've earned. OneKite folds all of it into one identity.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6 items-stretch">
          {/* The old way — cluttered, muted */}
          <div className="relative overflow-hidden rounded-3xl border border-black/10 bg-neutral-50 p-7 sm:p-8 flex flex-col">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-black/5 text-muted-foreground px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider">
              <Stack size={14} weight="bold" /> The old way
            </span>
            <p className="mt-4 font-display font-semibold text-xl">This is your wallet today.</p>
            <div className="mt-5 rounded-2xl overflow-hidden border border-black/10 relative">
              <img
                src="https://images.pexels.com/photos/32641818/pexels-photo-32641818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="A cluttered pile of separate plastic loyalty and store cards"
                className="w-full h-48 sm:h-52 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-50 via-neutral-50/10 to-transparent" />
            </div>
            <ul className="mt-7 space-y-3.5">
              {[
                { icon: Cardholder, t: "A different card for every shop" },
                { icon: DeviceMobile, t: "A separate app to download for each brand" },
                { icon: IdentificationBadge, t: "Register again everywhere you go" },
                { icon: Gift, t: "Points you forget you ever earned" },
              ].map((p) => (
                <li key={p.t} className="flex items-center gap-3 text-muted-foreground">
                  <span className="h-9 w-9 rounded-xl bg-black/5 flex items-center justify-center shrink-0"><p.icon size={18} weight="duotone" /></span>
                  <span className="text-sm">{p.t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* The OneKite way — calm, branded */}
          <div className="relative overflow-hidden rounded-3xl border border-primary/30 ring-1 ring-primary/20 bg-[hsl(160,42%,8%)] text-white p-7 sm:p-8 flex flex-col">
            <div className="absolute inset-0 opacity-70" style={{ backgroundImage: "radial-gradient(28rem 28rem at 100% -10%, rgba(52,211,153,0.22), transparent 60%)" }} />
            <div className="relative flex flex-col h-full">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/15 text-primary px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider">
                <Logo mark variant="white" className="h-3.5 w-auto" /> The OneKite way
              </span>
              <p className="mt-4 font-display font-semibold text-xl">This is your wallet on OneKite.</p>

              {/* single identity visual */}
              <div className="mt-6 flex items-start gap-4">
                <div className="rounded-2xl bg-white text-foreground p-4 shadow-2xl w-40 shrink-0">
                  <div className="flex items-center justify-between">
                    <Logo className="h-4" />
                  </div>
                  <div className="mt-3 rounded-xl bg-neutral-100 p-3 flex items-center justify-center">
                    <QrCode size={78} weight="duotone" className="text-foreground" />
                  </div>
                  <p className="mt-2.5 text-center font-display font-bold text-sm">@yourname</p>
                </div>
                <div className="flex-1 space-y-2 pt-1 min-w-0">
                  {[
                    { n: "Blinka's Coffee", p: "240" },
                    { n: "Ecowater", p: "530" },
                    { n: "ABC Pharmacy", p: "80" },
                  ].map((m) => (
                    <div key={m.n} className="flex items-center justify-between rounded-xl bg-white/8 border border-white/10 px-3 py-2">
                      <span className="text-xs text-white/85 truncate">{m.n}</span>
                      <span className="font-display font-bold text-primary text-sm shrink-0 ml-2">{m.p}</span>
                    </div>
                  ))}
                </div>
              </div>

              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3.5">
                {[
                  { icon: QrCode, t: "One QR for every business" },
                  { icon: Wallet, t: "Every balance, one place" },
                  { icon: Fingerprint, t: "A single secure identity" },
                  { icon: Sparkle, t: "Rewards that come to you" },
                ].map((b) => (
                  <li key={b.t} className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><b.icon size={16} weight="duotone" className="text-primary" /></span>
                    <span className="text-sm text-white/90">{b.t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- How OneKite Works ---------- */}
      <Section id="how" tint>
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>How OneKite works</Eyebrow>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">One simple identity wherever you go.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { icon: IdentificationBadge, step: "1", title: "Join OneKite", body: "Create your account once and receive your unique OneKite identity." },
            { icon: ScanSmiley, step: "2", title: "Scan at participating businesses", body: "Show your OneKite QR whenever you make a qualifying purchase." },
            { icon: Gift, step: "3", title: "Earn and enjoy", body: "Earn points from each business and redeem rewards directly with the business that issued them." },
          ].map((s, i) => (
            <div key={s.step} className="card-surface p-7 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-center justify-between mb-4">
                <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center"><s.icon size={24} weight="duotone" className="text-primary" /></div>
                <span className="font-display font-extrabold text-4xl text-primary/25">{s.step}</span>
              </div>
              <h3 className="font-display font-semibold text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3 text-sm font-medium">
          {["Join", "Scan", "Earn", "Redeem"].map((t, i, arr) => (
            <div key={t} className="flex items-center gap-3">
              <span className="rounded-full bg-primary/10 text-primary px-5 py-2.5 border border-primary/20">{t}</span>
              {i < arr.length - 1 && <ArrowRight size={18} className="text-primary/50" />}
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Your Businesses, One App ---------- */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <PhoneFrame><CustomerAppScreen /></PhoneFrame>
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow>Your businesses, one app</Eyebrow>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">Every relationship, beautifully organized.</h2>
            <div className="mt-8 space-y-3">
              {[
                { name: "Blinka's Coffee", points: "240 Points", note: "Free Coffee at 300 Points" },
                { name: "Ecowater", points: "530 Points", note: "50 Points earned this week" },
                { name: "ABC Pharmacy", points: "80 Points", note: "Merchant-specific balance" },
              ].map((m) => (
                <div key={m.name} className="card-surface p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.note}</p>
                  </div>
                  <span className="font-display font-bold text-primary">{m.points}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Every business runs its own points, rewards and loyalty rules — OneKite just gathers them into a single, effortless experience.
              <span className="text-foreground font-medium"> Your points always stay with the merchant that gave them, never pooled or moved between businesses.</span>
            </p>
          </div>
        </div>
      </Section>

      {/* ---------- For Customers ---------- */}
      <Section id="customers" tint>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-black/10 shadow-xl">
              <img
                src="https://images.pexels.com/photos/19863396/pexels-photo-19863396.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
                alt="A OneKite customer earning rewards on her phone over coffee"
                className="w-full h-80 lg:h-[30rem] object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 right-3 sm:right-6 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-black/5 p-3.5 flex items-center gap-3">
              <Logo mark className="h-8 w-auto" />
              <div>
                <p className="text-[11px] text-muted-foreground leading-none">Blinka's Coffee</p>
                <p className="font-display font-bold text-primary leading-tight mt-1">+20 points earned</p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <Eyebrow>For customers</Eyebrow>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">One account. Every day, more rewarding.</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                { icon: QrCode, t: "One QR across participating businesses" },
                { icon: Cardholder, t: "No more physical loyalty cards" },
                { icon: Coins, t: "Track all your merchant-specific points" },
                { icon: Gift, t: "Discover rewards you've unlocked" },
                { icon: Megaphone, t: "Receive offers from businesses you choose" },
                { icon: Wallet, t: "Keep memberships and future gift cards together" },
                { icon: Receipt, t: "Secure transaction history" },
                { icon: LockKey, t: "Control your personal information" },
              ].map((b) => (
                <div key={b.t} className="flex items-center gap-3 py-1.5">
                  <span className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><b.icon size={18} weight="duotone" className="text-primary" /></span>
                  <span className="text-sm">{b.t}</span>
                </div>
              ))}
            </div>
            <div className="mt-9">
              <Link to="/register">
                <Button size="lg" data-testid="customers-cta" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12">
                  Create Your OneKite Account <ArrowRight className="ml-2" size={18} weight="bold" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- For Businesses (dark, distinct) ---------- */}
      <section id="businesses" className="relative bg-[hsl(160,42%,8%)] text-white scroll-mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: "radial-gradient(40rem 40rem at 90% 0%, rgba(52,211,153,0.18), transparent 60%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-primary font-mono text-xs tracking-[0.25em] uppercase mb-4">For businesses · Customer engagement infrastructure</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">You make sales every day. But do you know who comes back?</h2>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">
              You know your sales — but not who's returning, and who's slipping away. OneKite helps you <span className="text-white font-medium">identify, engage, reward and retain</span> your customers. Loyalty is just the start.
            </p>

            <div className="mt-9 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Fingerprint, title: "Identify", body: "Tell regulars from first-timers with one secure QR." },
                { icon: Megaphone, title: "Engage", body: "Reach the right customers with targeted offers." },
                { icon: Gift, title: "Reward", body: "Points, rewards and loyalty — no app to build." },
                { icon: ChartLine, title: "Retain", body: "Spot drop-off and win customers back." },
              ].map((p) => (
                <div key={p.title} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center mb-3"><p.icon size={20} weight="duotone" className="text-primary" /></div>
                  <p className="font-display font-semibold text-lg text-white">{p.title}</p>
                  <p className="mt-1 text-sm text-white/70 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Multiple branches", "Staff management", "Customer insights", "Real-time activity", "Transaction history", "You own your customer data"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white/75">
                  <Check size={12} weight="bold" className="text-primary" /> {t}
                </span>
              ))}
            </div>

            <Link to="/business/register">
              <Button size="lg" data-testid="businesses-cta" className="mt-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12">
                Join OneKite Business <ArrowRight className="ml-2" size={18} weight="bold" />
              </Button>
            </Link>
          </div>
          <div className="flex justify-center lg:justify-end"><DashboardMockup /></div>
        </div>
      </section>

      {/* ---------- OneKite Business Experience ---------- */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>The OneKite Business platform</Eyebrow>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">Simple enough for the counter. Powerful enough for the business.</h2>
          <p className="mt-5 text-muted-foreground text-lg">Recognise, engage and retain customers — on the counter and behind the scenes.</p>
        </div>
        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
          <div className="flex flex-col items-center">
            <PhoneFrame><StaffAppScreen /></PhoneFrame>
            <div className="mt-6 text-center">
              <p className="font-display font-semibold text-lg flex items-center justify-center gap-2"><DeviceMobile size={20} className="text-primary" /> OneKite Business Mobile</p>
              <p className="text-sm text-muted-foreground">For staff: Scan Customer · Award Points · Redeem Reward · Today's Activity</p>
            </div>
          </div>
          <div className="card-surface p-8">
            <p className="font-display font-semibold text-lg flex items-center gap-2 mb-6"><ChartLine size={20} className="text-primary" /> OneKite Business Web</p>
            <p className="text-sm text-muted-foreground mb-6">For owners and managers.</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: UsersThree, t: "Customer insights" },
                { icon: Coins, t: "Loyalty programme settings" },
                { icon: Gift, t: "Rewards" },
                { icon: MapPin, t: "Branches" },
                { icon: IdentificationBadge, t: "Staff" },
                { icon: ChartLine, t: "Reports" },
                { icon: Megaphone, t: "Campaigns" },
                { icon: Receipt, t: "Transactions" },
              ].map((b) => (
                <div key={b.t} className="flex items-center gap-2.5 rounded-xl border border-black/8 p-3">
                  <b.icon size={18} weight="duotone" className="text-primary shrink-0" />
                  <span className="text-sm">{b.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- Built Around Trust ---------- */}
      <Section id="trust" tint>
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Built around trust</Eyebrow>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">Your identity stays yours.</h2>
          <p className="mt-5 text-muted-foreground text-lg">OneKite is designed around privacy and secure customer recognition.</p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: LockKey, title: "Your data stays private", body: "Businesses only see information related to their own relationship with you." },
            { icon: Fingerprint, title: "Secure QR identity", body: "Your QR identifies you securely without exposing balances or sensitive information." },
            { icon: Buildings, title: "Merchant independence", body: "Each business independently owns its loyalty programme and rewards." },
            { icon: ShieldCheck, title: "Verified transactions", body: "Points and redemptions are securely recorded through OneKite." },
          ].map((p, i) => (
            <div key={p.title} className="card-surface p-7 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center mb-4"><p.icon size={24} weight="duotone" className="text-primary" /></div>
              <h3 className="font-display font-semibold mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Final CTA ---------- */}
      <section className="relative bg-[hsl(160,42%,8%)] text-white overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(50rem 40rem at 50% 120%, rgba(52,211,153,0.22), transparent 60%)" }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 py-16 sm:py-24 text-center">
          <Handshake size={40} weight="duotone" className="text-primary mx-auto mb-6" />
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl leading-tight">Your next reward starts with OneKite.</h2>
          <p className="mt-5 text-white/70 text-lg">Join once. Use it across participating businesses.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button size="lg" data-testid="final-cta-customer" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12">
                Get OneKite <ArrowRight className="ml-2" size={18} weight="bold" />
              </Button>
            </Link>
            <Link to="/business/register">
              <Button size="lg" variant="outline" data-testid="final-cta-business" className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 px-7 h-12">
                Join as a Business
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-[hsl(160,44%,6%)] text-white/70">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Logo variant="white" className="h-12" />
            </div>
            <p className="text-sm max-w-xs">A unified customer engagement and digital identity platform that connects people with businesses through one trusted identity.</p>
          </div>
          <FooterCol title="Navigation" links={["Customers", "Businesses", "Features", "About"]} />
          <FooterCol title="Legal" links={["Privacy", "Terms", "Contact"]} />
          <FooterCol title="More" links={["OneKite Business", "Help Centre", "Developer Platform — Coming Soon"]} />
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <span>© 2026 OneKite. All rights reserved.</span>
            <span className="font-mono text-primary">onekite.io</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- helpers ---------- */
function Section({ children, id, tint, divider, white }) {
  return (
    <section id={id} className={`scroll-mt-20 ${white ? "bg-white" : tint ? "bg-secondary/40" : ""}`}>
      {divider && (
        <div className="max-w-7xl mx-auto px-5 sm:px-10">
          <div className="relative">
            <div className="h-px bg-gradient-to-r from-transparent via-black/15 to-transparent" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-4 h-8 w-8 rounded-full bg-background border border-black/10 flex items-center justify-center shadow-sm">
              <ArrowDown size={16} weight="bold" className="text-primary" />
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24">{children}</div>
    </section>
  );
}


function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-white font-semibold text-sm mb-4">{title}</p>
      <ul className="space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l}><span className="hover:text-white transition-colors cursor-pointer">{l}</span></li>
        ))}
      </ul>
    </div>
  );
}
